import {
  AUDIT_RESULT,
  AuditReadinessRecord,
  SmartControlTestRecord,
} from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import {
  CONTROL_STATUS,
  Control,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { getCheckResultWithStale } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.helpers'
import { CONTROL_TEST_NAMES } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.constants'
import { ControlDisplay } from './AuditScanModal.constants'

// creates map of controlId to SmartControlTestRecord based on the relevant tests. If no tests are provided, all of the tests are considered. This problem here is if not all tests have been run for a control.

export const createControlTestMap = (
  selectedTests: CONTROL_TEST_NAMES[],
  auditReadinessLookup?: Record<string, AuditReadinessRecord>,
): Record<string, SmartControlTestRecord> => {
  const controlTestMap: Record<string, SmartControlTestRecord> = {}
  if (!auditReadinessLookup) return controlTestMap

  const selectedTestsSet = new Set(selectedTests)

  Object.keys(auditReadinessLookup).forEach((controlId) => {
    const auditReadiness = auditReadinessLookup[controlId]
    const controlTests = auditReadiness.getControlTestsList()
    const filteredTests = selectedTests.length
      ? controlTests.filter((controlTest) =>
          selectedTestsSet.has(controlTest.getTestName() as CONTROL_TEST_NAMES),
        )
      : controlTests

    const testResult = getCheckResultWithStale(
      filteredTests,
      selectedTests.length,
    )

    controlTestMap[controlId] = new SmartControlTestRecord()
      .setControlId(controlId)
      .setIsStale(testResult.isStale)
      .setResult(testResult.result)
      .setIsMissingChecks(testResult.isMissingChecks)
  })

  return controlTestMap
}

export const getFilteredControlIds = (
  controlList: Control[],
  controlTestMap: Record<string, SmartControlTestRecord>,
  filterList: ControlDisplay[],
): string[] => {
  const filteredControls = getFilteredControls(
    controlList,
    controlTestMap,
    filterList,
  )
  return filteredControls.map((control) => control.getId())
}

export const getFilteredControls = (
  controlList: Control[],
  controlTestMap: Record<string, SmartControlTestRecord>,
  filterList: ControlDisplay[],
): Control[] => {
  // First, filter out controls that are not applicable
  const applicableControls = controlList.filter(
    (control) => control.getStatus() !== CONTROL_STATUS.NOTAPPLICABLE,
  )
  if (!filterList.length || filterList.includes(ControlDisplay.ALL))
    return applicableControls
  if (filterList.includes(ControlDisplay.NONE)) return []

  const filteredControls = applicableControls.filter((control) => {
    const controlTest =
      controlTestMap[control.getMigratedControlId() || control.getId()]
    const result = controlTest?.getResult()
    const isStale = controlTest?.getIsStale()
    const isPassing = result === AUDIT_RESULT.PASS
    const isFailing = result === AUDIT_RESULT.FAIL
    const isUnknown = result === AUDIT_RESULT.UNKNOWN
    const isNotRun = result === AUDIT_RESULT.NOT_RUN
    const isMissingChecks = controlTest?.getIsMissingChecks()

    if (filterList.includes(ControlDisplay.FAILED) && isFailing) return true
    if (filterList.includes(ControlDisplay.STALE) && isStale) return true
    if (filterList.includes(ControlDisplay.PASSED) && isPassing) return true
    if (filterList.includes(ControlDisplay.UNKNOWN) && isUnknown) return true
    if (filterList.includes(ControlDisplay.NOT_RUN) && isNotRun) return true
    if (filterList.includes(ControlDisplay.MISSING_CHECKS) && isMissingChecks)
      return true

    return false
  })
  return filteredControls
}
